import {
  Card,
  CardContent,
  CardHeading,
  DonutChart,
  FigureLabel,
} from '@/components/ui'
import {
  chartPalette,
  numberToCompactCurrency,
  numberToCurrency,
} from '@/utils'
import { formatAssetClass } from '../../labels'
import type { HoldingRow } from './types'

interface AssetAllocationProps {
  holdings: HoldingRow[]
}

// How many asset classes to name before folding the rest into "Other", so the legend stays readable.
const MAX_LEGEND = 5

// Where the money sits, by asset class. Built from the "as of" holdings so it always agrees with the
// market-value hero beside it.
const toSlices = (holdings: HoldingRow[]) => {
  const totals = new Map<string, number>()
  for (const holding of holdings) {
    const label = holding.assetClass || 'Unclassified'
    totals.set(label, (totals.get(label) ?? 0) + (holding.marketValue ?? 0))
  }

  return Array.from(totals.entries())
    .filter(([, value]) => value > 0)
    .sort((first, second) => second[1] - first[1])
    .map(([label, value], index) => ({
      // The engine stores asset classes as raw keys ('mutual_fund'); show them as readable text.
      label: formatAssetClass(label),
      value,
      color: chartPalette[index % chartPalette.length],
    }))
}

// The single largest position, the concentration signal: one outsized holding carrying much of the book.
const largestHolding = (holdings: HoldingRow[]) => {
  let largest: HoldingRow | null = null
  for (const holding of holdings) {
    if (!largest || (holding.marketValue ?? 0) > (largest.marketValue ?? 0))
      largest = holding
  }
  return largest
}

export const AssetAllocation = ({ holdings }: AssetAllocationProps) => {
  const slices = toSlices(holdings)
  const total = slices.reduce((running, slice) => running + slice.value, 0)
  const named = slices.slice(0, MAX_LEGEND)
  const otherValue = slices
    .slice(MAX_LEGEND)
    .reduce((running, slice) => running + slice.value, 0)

  const top = largestHolding(holdings)
  const topShare = top && total > 0 ? ((top.marketValue ?? 0) / total) * 100 : 0
  const share = (value: number) => (total > 0 ? (value / total) * 100 : 0)

  return (
    <Card className="flex flex-col">
      <CardHeading title="Allocation" />
      <CardContent className="flex flex-1 flex-col gap-4">
        {slices.length === 0 ? (
          <div className="flex h-full min-h-[180px] items-center justify-center text-sm text-ink-subtle">
            No holdings to break down.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4">
              <div className="w-[140px] shrink-0">
                <DonutChart
                  data={slices}
                  size={140}
                  innerRadius={0.64}
                  showLegend={false}
                  centerValue={numberToCompactCurrency(total)}
                  centerLabel="Total"
                  valueFormatter={(value) => numberToCurrency(value)}
                />
              </div>
              <ul className="min-w-0 flex-1 space-y-1.5 text-sm">
                {named.map((slice) => (
                  <li key={slice.label} className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: slice.color }}
                    />
                    <span className="truncate text-blue-6">{slice.label}</span>
                    <span className="ml-auto shrink-0 tabular-nums text-ink-subtle">
                      {share(slice.value).toFixed(0)}%
                    </span>
                  </li>
                ))}
                {otherValue > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-blue-3" />
                    <span className="truncate text-ink-subtle">Other</span>
                    <span className="ml-auto shrink-0 tabular-nums text-ink-subtle">
                      {share(otherValue).toFixed(0)}%
                    </span>
                  </li>
                )}
              </ul>
            </div>

            {top && (
              <div className="mt-auto flex items-center justify-between gap-2 border-t pt-4">
                <FigureLabel className="shrink-0">Top position</FigureLabel>
                <span className="truncate text-xs text-blue-7">
                  <span className="font-semibold">
                    {top.symbol || top.description || 'Largest holding'}
                  </span>
                  <span className="text-ink-subtle">
                    , {topShare.toFixed(0)}% of value
                  </span>
                </span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
